import type { DialogTone } from "./AppDialog";

// Options d'un dialogue. `confirmLabel`/`cancelLabel` ont des défauts FR pour
// que l'appel courant tienne en une ligne.
export type ConfirmOptions = {
  title:         string;
  message?:      string;
  tone?:         DialogTone;
  details?:      { label: string; value: string }[];
  confirmLabel?: string;
  cancelLabel?:  string;
  /** Action destructrice → bouton rouge (annuler une course, supprimer…). */
  danger?:       boolean;
  /** Dialogue purement informatif : un seul bouton, pas d'annulation. */
  alertOnly?:    boolean;
  /** Décision obligatoire : ni tap sur le fond ni croix. */
  blocking?:     boolean;
};

export type PendingDialog = {
  opts:    ConfirmOptions;
  resolve: (confirmed: boolean) => void;
};

/**
 * File d'attente des dialogues — logique PURE, sans React.
 *
 * Extraite du provider pour être testable sans rendu : c'est ici que vivent les
 * deux risques réels du pattern, et un bug sur l'un ou l'autre fige l'écran
 * appelant (une promesse jamais résolue bloque un `await` pour toujours) :
 *
 * 1. **Demandes concurrentes** — deux `confirm()` quasi simultanés (double tap,
 *    deux effets). Sans file, le second écraserait le premier et la promesse du
 *    premier resterait pendante.
 * 2. **Démontage pendant l'attente** — navigation alors qu'un dialogue est
 *    ouvert. Toutes les promesses en attente doivent être résolues.
 *
 * L'instance ne fait que gérer l'ordre : c'est l'appelant (le provider) qui
 * décide quand afficher, via `getCurrent()`.
 */
export class DialogQueue {
  private current: PendingDialog | null = null;
  private waiting: PendingDialog[] = [];

  /** Dialogue à afficher, ou `null` si aucun. */
  getCurrent(): PendingDialog | null {
    return this.current;
  }

  /** Nombre de dialogues en attente derrière le courant (diagnostic/tests). */
  get pendingCount(): number {
    return this.waiting.length;
  }

  /**
   * Ajoute une demande. Devient le dialogue courant si aucun n'est affiché,
   * sinon prend la file.
   */
  push(pending: PendingDialog): void {
    if (this.current) {
      this.waiting.push(pending);
      return;
    }
    this.current = pending;
  }

  /**
   * Répond au dialogue courant et passe au suivant.
   * Retourne le nouveau dialogue courant (ou `null`).
   */
  settle(confirmed: boolean): PendingDialog | null {
    const active = this.current;
    this.current = this.waiting.shift() ?? null;
    // Résolution APRÈS avoir mis à jour l'état : si l'appelant relance un
    // `confirm()` dans son `.then()`, la file est déjà cohérente.
    active?.resolve(confirmed);
    return this.current;
  }

  /**
   * Vide la file en résolvant TOUT à `false` (démontage).
   * Refus par défaut : on ne confirme jamais une action par accident.
   */
  clear(): void {
    const all = this.current ? [this.current, ...this.waiting] : [...this.waiting];
    this.current = null;
    this.waiting = [];
    all.forEach((p) => p.resolve(false));
  }
}
